import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { printElementToPdf } from "../../../../utils/printElement";
import CloseIcon from "@mui/icons-material/Close";
import MedicationIcon from "@mui/icons-material/Medication";
import PrintIcon from "@mui/icons-material/Print";
import ReplayIcon from "@mui/icons-material/Replay";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import baseUrl from "../../../../api/baseUrl";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../redux/loader/loaderActions";
import {
  setPresPrintaction,
  setPresPrintclinic,
  setPresPrintPatient,
  setPresPrintTreatment,
} from "../../../../redux/printstate/printActions";
import PrePrint from "../../../setting/manageprint/preprint/PrePrint";
import {
  formatDoctorName,
  formatDoctorProfessionalDetails,
} from "../../../setting/manageprint/preprint/prescriptionPrintFields";
import {
  validatePrescriptionForm,
  type PrescriptionFormData,
} from "../../../manage/prescription/prescriptionFormUtils";
import {
  resolveDoseValue,
  provideStatusLabel,
} from "../../../manage/prescription/prescriptionDispenseUtils";

interface DrugHistoryModalProps {
  treatmentId: string;
  patientName?: string;
  invoiceNo?: string;
  onClose: () => void;
}

interface DrugRow {
  _id: string;
  drugsId: string;
  drugs: string;
  type: string;
  dose: string;
  doseId?: string;
  frequency: string;
  duration: string;
  qty?: number | string;
  dispense?: boolean;
  productId?: string;
  settled?: boolean;
  source: "current" | "past";
  treatmentId?: string;
  createdAt?: string;
  doctorName?: string;
}

interface DateWiseGroup {
  date: string;
  categories: { category: string; items: DrugRow[] }[];
}

const CHECKBOX_CLASS =
  "appearance-none w-3.5 h-3.5 shrink-0 rounded border-2 border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:ring-offset-0 checked:border-teal-500 checked:bg-teal-500 checked:bg-center checked:bg-no-repeat checked:bg-[length:12px_12px] checked:[background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3.5 8.5l3 3 6-6'/%3E%3C/svg%3E\")]";

const toDateKey = (value?: string) => {
  if (!value) return "Unknown";
  const key = String(value).split("T")[0];
  return key || "Unknown";
};

const flattenGroup = (group: DateWiseGroup): DrugRow[] =>
  group.categories.flatMap((c) => c.items);

function resolveDrugsId(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string" || typeof raw === "number") return String(raw);
  if (typeof raw === "object" && raw !== null && "_id" in raw) {
    return String((raw as { _id?: unknown })._id ?? "");
  }
  return "";
}

function cleanDoseArray(data: any[]): DrugRow[] {
  return (data || []).map((item: any) => ({
    _id: item?._id || "",
    drugsId: resolveDrugsId(item?.drugsId?._id ?? item?.drugsId),
    dose: resolveDoseValue(item),
    doseId: item?.doseId || "",
    drugs: item?.drugsId?.drugs || item?.drugs || "",
    type: item?.drugsId?.type || item?.type || "",
    duration: item?.duration || "",
    frequency: item?.frequency || "",
    qty: item?.qty,
    dispense: Boolean(item?.dispense),
    productId: item?.productId
      ? String(item.productId?._id ?? item.productId)
      : "",
    settled: Boolean(item?.settled),
    createdAt: item?.createdAt || "",
    source: "current" as const,
  }));
}

function buildReusePayload(item: DrugRow): PrescriptionFormData {
  const doseKey = item.doseId || item.dose || "";
  return {
    drugsId: item.drugsId,
    drugsIds: `${item.drugsId}/${item.drugs || ""}/${item.type || ""}`,
    doseId: doseKey,
    doseIds: doseKey,
    dose: item.dose || "",
    frequency: item.frequency || "",
    duration: String(item.duration || ""),
    drugs: item.drugs || "",
    type: item.type || "",
    // The doctor's Provide signal carries through a reuse
    dispense: Boolean(item.dispense),
  };
}

const DrugHistoryModal: React.FC<DrugHistoryModalProps> = ({
  treatmentId,
  patientName,
  invoiceNo,
  onClose,
}) => {
  const dispatch = useDispatch();
  const printRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [reusing, setReusing] = useState(false);
  const [currentDrugs, setCurrentDrugs] = useState<DrugRow[]>([]);
  const [pastDrugs, setPastDrugs] = useState<DrugRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [printData, setPrintData] = useState<unknown>("");

  const dateWiseGroups = useMemo<DateWiseGroup[]>(() => {
    const byDate = new Map<string, DrugRow[]>();

    const pushRow = (date: string, row: DrugRow) => {
      const list = byDate.get(date) ?? [];
      list.push(row);
      byDate.set(date, list);
    };

    currentDrugs.forEach((item) => {
      pushRow(toDateKey(item.createdAt), {
        ...item,
        source: "current",
        treatmentId,
      });
    });

    pastDrugs.forEach((item) => {
      pushRow(toDateKey(item.createdAt), item);
    });

    return Array.from(byDate.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, items]) => {
        const byCategory = new Map<string, DrugRow[]>();
        items.forEach((row) => {
          const category = (row.type || "Other").trim() || "Other";
          const list = byCategory.get(category) ?? [];
          list.push(row);
          byCategory.set(category, list);
        });
        const categories = Array.from(byCategory.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, categoryItems]) => ({
            category,
            items: categoryItems,
          }));
        return { date, categories };
      });
  }, [currentDrugs, pastDrugs, treatmentId]);

  const loadDrugs = useCallback(async () => {
    if (!treatmentId) return;
    setLoading(true);
    try {
      const [currentRes, pastRes] = await Promise.all([
        baseUrl.get("treatment/find-treatment", {
          headers: { treatmentid: treatmentId },
        }),
        baseUrl.get("treatment/findPastPrescriptionByTreatmentId", {
          headers: { treatmentid: treatmentId },
        }),
      ]);

      const current = cleanDoseArray(currentRes.data?.data?.prescription);
      const pastRaw: any[] = Array.isArray(pastRes.data?.data)
        ? pastRes.data.data
        : [];
      const past: DrugRow[] = pastRaw.map((item) => ({
        _id: String(item?._id ?? ""),
        drugsId: resolveDrugsId(item?.drugsId),
        drugs: item?.drugs || "",
        type: item?.type || "",
        dose: item?.dose || "",
        doseId: item?.doseId || "",
        frequency: item?.frequency || "",
        duration: item?.duration || "",
        qty: item?.qty,
        dispense: Boolean(item?.dispense),
        productId: item?.productId
          ? String(item.productId?._id ?? item.productId)
          : "",
        settled: Boolean(item?.settled),
        doctorName: item?.doctorName,
        source: "past" as const,
        treatmentId: item?.treatmentId,
        createdAt: item?.visitDate || item?.createdAt || "",
      }));

      setCurrentDrugs(current);
      setPastDrugs(past);

      const allIds = [
        ...current.map((d) => d._id),
        ...past.map((row) => row._id).filter(Boolean),
      ];
      setSelectedIds(allIds);
    } catch (error) {
      console.error(error);
      toast.error("Could not load drug history.");
      setCurrentDrugs([]);
      setPastDrugs([]);
    } finally {
      setLoading(false);
    }
  }, [treatmentId]);

  useEffect(() => {
    loadDrugs();
  }, [loadDrugs]);

  const dispatchPrintActions = useCallback(
    (e: any) => {
      e?.other?.clinicId?.prescriptionBill?.forEach((element: any) => {
        const { title, top, left, show, size, width, height } = element;
        if (top)
          dispatch(
            setPresPrintaction({ name: title, target: "top", value: top })
          );
        if (left)
          dispatch(
            setPresPrintaction({ name: title, target: "left", value: left })
          );
        if (show !== undefined)
          dispatch(
            setPresPrintaction({ name: title, target: "show", value: show })
          );
        if (size)
          dispatch(
            setPresPrintaction({ name: title, target: "size", value: size })
          );
        if (width)
          dispatch(
            setPresPrintaction({ name: title, target: "width", value: width })
          );
        if (height)
          dispatch(
            setPresPrintaction({ name: title, target: "height", value: height })
          );
      });
      dispatch(
        setPresPrintclinic({
          clinicName: e?.other?.clinicId?.title,
          tpNo: e?.other?.clinicId?.tpNo,
          address: e?.other?.clinicId?.address,
          drName: formatDoctorName(e?.other?.doctorId),
          professionalDetails: formatDoctorProfessionalDetails(e?.other?.doctorId),
          printImages: e?.other?.clinicId?.printImages,
          headerImages: e?.other?.clinicId?.headerImages,
          footerImages: e?.other?.clinicId?.footerImages,
        })
      );
      dispatch(
        setPresPrintPatient({
          invoiceNo: e?.other?.invoiceNo,
          regNo: e?.patientData?.regNo,
          fullName: e?.patientData?.fullName,
          mobileNo: e?.patientData?.mobileNo,
          gender: e?.patientData?.gender,
          dob: e?.patientData?.dob,
          createdAt: new Date().toISOString(),
        })
      );
      dispatch(setPresPrintTreatment(cleanDoseArray(e?.prescription)));
    },
    [dispatch]
  );

  const handleDownloadPdf = useCallback(async () => {
    if (!printRef.current) return;
    try {
      await printElementToPdf(printRef.current);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (printData) {
      handleDownloadPdf();
    }
  }, [printData, handleDownloadPdf]);

  const printItems = useCallback(
    async (items: DrugRow[], dateLabel?: string) => {
      if (items.length === 0) {
        toast.error("No prescriptions to print.");
        return;
      }

      const currentIds = items
        .filter((item) => item.source === "current" && item._id)
        .map((item) => item._id);
      const layoutTreatmentId =
        (currentIds.length > 0 && treatmentId) ||
        items.find((item) => item.treatmentId)?.treatmentId ||
        treatmentId;
      const layoutIds =
        currentIds.length > 0
          ? currentIds
          : items.filter((item) => item._id).map((item) => item._id);

      if (!layoutTreatmentId || layoutIds.length === 0) {
        toast.error("Unable to print prescriptions.");
        return;
      }

      try {
        dispatch(setloadeActive());
        const response = await baseUrl.get(
          "/client/treatments/prescription/getPrescriptionById",
          {
            headers: {
              treatmentid: layoutTreatmentId,
              prescrip: layoutIds,
            },
          }
        );

        dispatchPrintActions(response.data);
        dispatch(
          setPresPrintTreatment(
            items.map((item) => ({
              _id: item._id,
              drugs: item.drugs,
              type: item.type,
              dose: item.dose,
              frequency: item.frequency,
              duration: item.duration,
              createdAt: dateLabel || toDateKey(item.createdAt),
            }))
          )
        );
        setPrintData(response.data);
      } catch (error) {
        console.error(error);
        toast.error("Failed to print prescriptions.");
      } finally {
        dispatch(setloadeDeactive());
      }
    },
    [dispatch, dispatchPrintActions, treatmentId]
  );

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setSelectedIds((prev) =>
      checked ? [...prev, name] : prev.filter((id) => id !== name)
    );
  };

  const toggleDateGroupSelection = (group: DateWiseGroup, checked: boolean) => {
    const ids = flattenGroup(group).map((item) => item._id);
    setSelectedIds((prev) => {
      if (checked) return Array.from(new Set([...prev, ...ids]));
      return prev.filter((id) => !ids.includes(id));
    });
  };

  const handlePrintDateWise = async (group: DateWiseGroup) => {
    const selectedInDate = flattenGroup(group).filter((item) =>
      selectedIds.includes(item._id)
    );
    if (selectedInDate.length === 0) {
      toast.error("Select prescriptions to print.");
      return;
    }
    await printItems(selectedInDate, group.date);
  };

  const handlePrintAllSelected = async () => {
    const allItems = dateWiseGroups.flatMap(flattenGroup);
    const selected = allItems.filter((item) => selectedIds.includes(item._id));
    if (selected.length === 0) {
      toast.error("Select prescriptions to print.");
      return;
    }
    await printItems(selected);
  };

  const reuseItems = useCallback(
    async (items: DrugRow[]) => {
      if (items.length === 0) {
        toast.error("Select drug(s) to reuse.");
        return;
      }

      // Prefer current-list rows when the same drug appears in past + current.
      const byDrugId = new Map<string, DrugRow>();
      for (const item of items) {
        if (!item.drugsId) continue;
        const existing = byDrugId.get(item.drugsId);
        if (!existing || item.source === "current") {
          byDrugId.set(item.drugsId, item);
        }
      }

      const uniqueItems = Array.from(byDrugId.values());
      if (uniqueItems.length === 0) {
        toast.error("Selected drugs are missing drug references.");
        return;
      }

      setReusing(true);
      dispatch(setloadeActive());
      try {
        let reused = 0;
        // Reverse so the first selected ends up first after move-to-front updates.
        for (const item of [...uniqueItems].reverse()) {
          const existingCurrent = currentDrugs.find(
            (d) => String(d.drugsId) === String(item.drugsId) && !d.settled
          );

          if (existingCurrent?._id) {
            await baseUrl.patch(
              "/client/treatments/prescription/updatePrescription",
              {
                drugsId: existingCurrent.drugsId || item.drugsId,
                doseId:
                  item.doseId ||
                  existingCurrent.doseId ||
                  item.dose ||
                  existingCurrent.dose ||
                  "",
                frequency: item.frequency || existingCurrent.frequency || "",
                duration: String(
                  item.duration || existingCurrent.duration || ""
                ),
                dispense: Boolean(item.dispense || existingCurrent.dispense),
              },
              {
                headers: {
                  treatmentid: treatmentId,
                  prescriptionid: existingCurrent._id,
                },
              }
            );
            reused += 1;
            continue;
          }

          const payload = buildReusePayload(item);
          const validation = validatePrescriptionForm(payload);
          if (!validation.valid) {
            throw new Error(`${item.drugs || "Drug"}: ${validation.message}`);
          }

          await baseUrl.patch(
            "/client/treatments/prescription/addprescription",
            validation.payload,
            { headers: { treatmentid: treatmentId } }
          );
          reused += 1;
        }

        toast.success(
          reused === 1
            ? "1 drug reused on this treatment."
            : `${reused} drugs reused on this treatment.`
        );
        await loadDrugs();
      } catch (error: any) {
        console.error(error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to reuse selected drugs."
        );
      } finally {
        setReusing(false);
        dispatch(setloadeDeactive());
      }
    },
    [currentDrugs, dispatch, loadDrugs, treatmentId]
  );

  const reuseSelectedDrugs = useCallback(async () => {
    const allItems = dateWiseGroups.flatMap(flattenGroup);
    const selected = allItems.filter((item) => selectedIds.includes(item._id));
    await reuseItems(selected);
  }, [dateWiseGroups, reuseItems, selectedIds]);

  const reuseDateGroup = useCallback(
    async (group: DateWiseGroup) => {
      const selectedInDate = flattenGroup(group).filter((item) =>
        selectedIds.includes(item._id)
      );
      await reuseItems(selectedInDate);
    },
    [reuseItems, selectedIds]
  );

  const totalCount = currentDrugs.length + pastDrugs.length;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-teal-500/5 to-transparent px-4 py-3 dark:border-slate-600">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900/40">
              <MedicationIcon
                className="text-teal-600 dark:text-teal-400"
                sx={{ fontSize: 18 }}
              />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Drug History
              </h2>
              <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                {[patientName, invoiceNo ? `#${invoiceNo}` : null]
                  .filter(Boolean)
                  .join(" · ") || "Date-wise prescriptions"}
                {totalCount > 0 && (
                  <span className="ml-1 text-teal-600 dark:text-teal-400">
                    · {totalCount} drug{totalCount === 1 ? "" : "s"}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={reuseSelectedDrugs}
              disabled={selectedIds.length === 0 || loading || reusing}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-teal-100 px-3 text-xs font-medium text-teal-700 transition-all hover:bg-teal-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-teal-900/40 dark:text-teal-300 dark:hover:bg-teal-900/60"
              title="Reuse selected drugs on this treatment"
            >
              <ReplayIcon sx={{ fontSize: 14 }} />
              Reuse
              {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
            </button>
            <button
              type="button"
              onClick={handlePrintAllSelected}
              disabled={selectedIds.length === 0 || loading}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-600 px-3 text-xs font-medium text-white transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PrintIcon sx={{ fontSize: 14 }} />
              Print
              {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-600 dark:text-slate-300 dark:hover:bg-slate-500"
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="py-10 text-center text-xs text-slate-500 dark:text-slate-400">
              Loading drug history…
            </div>
          ) : dateWiseGroups.length === 0 ? (
            <div className="py-10 text-center text-slate-400 dark:text-slate-500">
              <CalendarMonthIcon sx={{ fontSize: 36 }} className="mb-2" />
              <p className="text-xs">No prescriptions found for this treatment.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dateWiseGroups.map((group) => {
                const groupItems = flattenGroup(group);
                const groupIds = groupItems.map((item) => item._id);
                const selectedInGroup = groupIds.filter((id) =>
                  selectedIds.includes(id)
                );
                const allGroupSelected =
                  groupIds.length > 0 &&
                  selectedInGroup.length === groupIds.length;

                return (
                  <div
                    key={group.date}
                    className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-600"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-600 dark:bg-slate-700/50">
                      <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200">
                        <input
                          type="checkbox"
                          className={CHECKBOX_CLASS}
                          checked={allGroupSelected}
                          onChange={(e) =>
                            toggleDateGroupSelection(group, e.target.checked)
                          }
                        />
                        <CalendarMonthIcon sx={{ fontSize: 14 }} />
                        {group.date}
                        <span className="font-normal text-slate-500 dark:text-slate-400">
                          · {groupItems.length} drug
                          {groupItems.length === 1 ? "" : "s"}
                        </span>
                      </label>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => reuseDateGroup(group)}
                          disabled={selectedInGroup.length === 0 || reusing}
                          className="inline-flex items-center gap-1 rounded-lg bg-teal-100 px-2.5 py-1 text-[11px] font-medium text-teal-700 transition-all hover:bg-teal-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-teal-900/40 dark:text-teal-300 dark:hover:bg-teal-900/60"
                          title={`Reuse selected for ${group.date}`}
                        >
                          <ReplayIcon sx={{ fontSize: 13 }} />
                          Reuse
                          {selectedInGroup.length > 0
                            ? ` (${selectedInGroup.length})`
                            : ""}
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePrintDateWise(group)}
                          className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-600 px-2.5 py-1 text-[11px] font-medium text-white transition-all hover:shadow-md"
                          title={`Print selected for ${group.date}`}
                        >
                          <PrintIcon sx={{ fontSize: 13 }} />
                          Print
                          {selectedInGroup.length > 0
                            ? ` (${selectedInGroup.length})`
                            : ""}
                        </button>
                      </div>
                    </div>

                    <div className="divide-y divide-slate-100 dark:divide-slate-700">
                      {group.categories.map(({ category, items }) => (
                        <div key={`${group.date}-${category}`}>
                          <div className="bg-teal-50/80 px-3 py-1.5 dark:bg-teal-900/20">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300">
                              {category}
                            </span>
                            <span className="ml-2 text-[10px] text-teal-600/80 dark:text-teal-400/80">
                              {items.length}
                            </span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-white dark:bg-slate-800">
                                <tr>
                                  <th className="w-10 px-3 py-1.5 text-left text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">
                                    Sel
                                  </th>
                                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">
                                    Drug
                                  </th>
                                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">
                                    Dose
                                  </th>
                                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">
                                    Freq
                                  </th>
                                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">
                                    Days
                                  </th>
                                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">
                                    Qty
                                  </th>
                                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {items.map((item) => (
                                  <tr
                                    key={item._id}
                                    className={
                                      selectedIds.includes(item._id)
                                        ? "bg-teal-50/60 dark:bg-teal-900/20"
                                        : ""
                                    }
                                  >
                                    <td className="px-3 py-2">
                                      <input
                                        type="checkbox"
                                        name={item._id}
                                        checked={selectedIds.includes(item._id)}
                                        onChange={handleCheckboxChange}
                                        className={CHECKBOX_CLASS}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-200">
                                      {item.drugs || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                                      <span className="rounded-md bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                                        {item.dose}
                                        {item.type}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                                      {item.frequency || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                                      {item.duration || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                                      {item.qty != null && item.qty !== ""
                                        ? item.qty
                                        : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                                      {provideStatusLabel(item)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ position: "absolute", left: "-9999px" }}>
        <div ref={printRef} style={{ display: "none" }}>
          <PrePrint />
        </div>
      </div>
    </div>
  );
};

export default DrugHistoryModal;
